import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authApi } from '../services/api';
import { useAuth, clearSignedOutFlag } from '../contexts/AuthContext';
import { IDLE_LOGOUT_REASON } from '../config/session';
import Logo from '../components/Logo';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Idle-timeout notice (Foundation PRD v0.3 US-F01). When the idle timer
  // logs the user out it bounces here with ?reason=idle so we explain why.
  const idleSignedOut = searchParams.get('reason') === IDLE_LOGOUT_REASON;

  useEffect(() => {
    // 2026-05-08 — user landed on the login page; clear any stale
    // signed-out intent so a successful login goes through to the dashboard.
    clearSignedOutFlag();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { status, data } = await authApi.login(email, password);

      // No MFA enrolled yet → go to the enrolment (setup) screen, not the
      // verify screen. Mirrors InviteActivation.js. A user who set a password
      // via invite but never chose an MFA method lands here on next login;
      // routing them to /mfa (verify) is a dead end because no code is sent.
      if (status === 200 && data.mfa_enrolment_required) {
        navigate('/mfa/enrol');
        return;
      }

      if (status === 200 && data.mfa_required) {
        sessionStorage.setItem('mfa_method', data.mfa_method);
        navigate('/mfa');
        return;
      }

      // Full session already issued by the backend without an MFA challenge.
      // Two cases land here:
      //   - stakeholder role bypass (shared board/c-suite read-only login), and
      //   - MFA_BYPASS_EMAILS allowlist (demo/test logins that keep their normal
      //     role, e.g. owner_contact, but skip OTP).
      // Same handling either way: refresh AuthContext so /auth/me populates user
      // state, then land on Home. (For stakeholders, the IdentifyMe modal on Home
      // blocks the UI until they enter their name + role.) Without this general
      // branch, a non-stakeholder bypass login fell through to the error path and
      // flashed "Something went wrong" before the session-cookie redirect kicked in.
      if (status === 200 && data.authenticated) {
        await refresh();
        navigate('/');
        return;
      }

      if (status === 200 && data.first_login) {
        setError(data.message);
        return;
      }

      // ACCESS REMOVED (blacklisted owner) — the server refuses with 403
      // { reason: 'access_removed' } and NO `error` string. Deliberately SILENT
      // to the person (operator's call, 2026-09-16): they simply stay on the
      // login page with no banner. The refusal is logged and audited server-side,
      // which is where the operator looks for it.
      //
      // Handled explicitly rather than by falling through, because the generic
      // line below would otherwise show "Something went wrong — please try
      // again." and invite a support call about a decision that was deliberate.
      if (data && data.reason === 'access_removed') {
        setError('');
        return;
      }

      setError(data.error || 'Something went wrong — please try again.');
    } catch {
      setError("Something went wrong — we're looking into it.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <Logo width={180} style={{ margin: '0 auto' }} />
          <div style={styles.sub}>Owner Portal</div>
        </div>

        <h1 style={styles.title}>Welcome back</h1>
        <p style={styles.subtitle}>Sign in to manage my properties.</p>

        {idleSignedOut && (
          <div style={styles.notice} role="status">
            You were signed out due to inactivity. Please sign in again to continue.
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={styles.input} placeholder="you@example.com" required autoComplete="email" />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrap}>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} style={{ ...styles.input, paddingRight: '44px' }} placeholder="Your password" required autoComplete="current-password" />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                style={styles.eyeButton}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" style={{ ...styles.button, opacity: loading ? 0.7 : 1 }} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div style={styles.footer}>
          <a href="#/help" style={styles.link}>Need help signing in?</a>
          <span style={styles.divider}>·</span>
          <a href="#/staff-login" style={styles.link}>Staff login</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--cream)', padding: '20px' },
  card: { backgroundColor: 'var(--white)', borderRadius: '12px', padding: '48px 44px', width: '100%', maxWidth: '420px', boxShadow: 'var(--shadow-card)', border: '1px solid var(--gray-100)' },
  brandRow: { textAlign: 'center', marginBottom: '28px' },
  brand: { fontSize: '28px', fontWeight: '700', color: 'var(--navy)', letterSpacing: '-0.01em' },
  sub: { fontSize: '10px', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.14em', marginTop: '6px', fontWeight: '500' },
  title: { fontSize: '22px', fontWeight: '700', color: 'var(--navy)', margin: '0 0 6px 0', textAlign: 'center', letterSpacing: '-0.01em' },
  subtitle: { fontSize: '14px', color: 'var(--gray-600)', margin: '0 0 28px 0', textAlign: 'center' },
  form: { display: 'flex', flexDirection: 'column', gap: '18px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '500', color: 'var(--gray-800)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--gray-200)', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.15s' },
  passwordWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  eyeButton: { position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', padding: 0, background: 'none', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'var(--gray-400)' },
  error: { backgroundColor: 'var(--red-light)', border: '1px solid var(--red)', borderRadius: '8px', padding: '11px 14px', fontSize: '13px', color: 'var(--red)' },
  notice: { backgroundColor: 'var(--cream)', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '11px 14px', fontSize: '13px', color: 'var(--gray-800)', marginBottom: '20px' },
  button: { backgroundColor: 'var(--navy)', color: 'var(--white)', border: 'none', borderRadius: '6px', padding: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', width: '100%', letterSpacing: '0.01em', boxShadow: '0 2px 8px rgba(19,33,57,0.22)' },
  footer: { marginTop: '22px', textAlign: 'center', fontSize: '13px', color: 'var(--gray-600)' },
  link: { color: 'var(--teal)', textDecoration: 'none' },
  divider: { margin: '0 10px', color: 'var(--gray-200)' }
};
